import * as React from 'react';
import classnames from 'classnames';
import { Switch, renderIcon } from '@ixicare/ui';
import { Field, FieldValue, FieldValues, Section } from './schema';
import FieldRenderer from './fields/FieldRenderer';
import styles from './styles.module.css';
import { t } from '~i18n';
import { applySubProfile, deriveSubProfile } from './utils/subProfile';
import { GatingContext } from './utils/gating';
import { DifferingKeyDetail } from './fields/FieldWrapper';

interface Props {
  sectionId: string;
  section: Section;
  values: FieldValues;
  initialValues: FieldValues;
  errors: Record<string, string>;
  gating: GatingContext;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onChange: (id: string, value: FieldValue) => void;
  differingDetail?: Map<string, DifferingKeyDetail[]>;
  // Bulk mode: device-level fields (e.g. phone_number) are per-device and hidden.
  hideDeviceModelLevel?: boolean;
}

// Map (section.cols, field.span) onto the 12-column @ixicare/ui grid.
// section.cols=2 → each field is col-6; span=2 → col-12. cols=4 → col-3,
// span=2 → col-6, etc. Capped at 12.
const colSpan = (sectionCols: number | undefined, fieldSpan?: number) => {
  const cols = sectionCols ?? 2;
  const base = 12 / cols;
  return Math.min(12, base * (fieldSpan ?? 1));
};

const SectionCard: React.FC<Props> = ({
  sectionId,
  section,
  values,
  initialValues,
  errors,
  gating,
  collapsed,
  onToggleCollapsed,
  onChange,
  differingDetail,
  hideDeviceModelLevel,
}) => {
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);

  // Advanced fields are hidden until the section's advanced toggle is on.
  // Device-level fields are hidden in bulk mode (they're per-device).
  const visibleFields = section.fields.filter(
    (f) => (showAdvanced || !f.advanced) && !(hideDeviceModelLevel && f.device_model_level)
  );

  const changedCount = React.useMemo(
    () =>
      section.fields.filter((f) => values[f.id] !== initialValues[f.id] && initialValues[f.id] !== undefined).length,
    [section.fields, values, initialValues]
  );

  const differingCount = differingDetail ? visibleFields.filter((f) => differingDetail.has(f.id)).length : 0;

  const renderField = (field: Field) => (
    <div key={field.id} className={classnames(styles.fieldCell, `col-${colSpan(section.cols, field.span)}`)}>
      <FieldRenderer
        field={field}
        values={values}
        initialValue={initialValues[field.id]}
        initialValues={initialValues}
        error={errors[field.id]}
        onChange={onChange}
        gating={gating}
        differingDetail={differingDetail}
      />
    </div>
  );

  let body: React.ReactNode;
  if (section.sub_sections && section.sub_sections.length) {
    const groups = new Map<string, Field[]>();
    section.sub_sections.forEach((s) => groups.set(s.id, []));
    const ungrouped: Field[] = [];
    visibleFields.forEach((f) => {
      if (f.sub_section && groups.has(f.sub_section)) {
        groups.get(f.sub_section)!.push(f);
      } else {
        ungrouped.push(f);
      }
    });
    body = (
      <>
        {ungrouped.length > 0 && (
          <div className={styles.subSection}>
            <div className="row">{ungrouped.map(renderField)}</div>
          </div>
        )}
        {section.sub_sections.map((s) => {
          const fields = groups.get(s.id) || [];
          if (fields.length === 0) return null;
          return (
            <div className={styles.subSection} key={s.id}>
              <div className={styles.subHead}>{t(s.title_key, s.id)}</div>
              <div className="row">{fields.map(renderField)}</div>
            </div>
          );
        })}
      </>
    );
  } else {
    body = <div className="row">{visibleFields.map(renderField)}</div>;
  }

  return (
    <section className={classnames(styles.section, collapsed && styles.collapsed)} data-sec={sectionId}>
      <div className={styles.sectionHead} onClick={onToggleCollapsed}>
        {section.icon && (
          <span
            className={styles.sectionIcon}
            style={section.icon_color ? { color: section.icon_color } : undefined}
            aria-hidden
          >
            {renderIcon(section.icon, undefined)}
          </span>
        )}
        <h2>{t(section.title_key, sectionId)}</h2>
        <span className={styles.sectionCount}>{visibleFields.length} fields</span>
        {changedCount > 0 && <span className={styles.changedDot}>● {changedCount} changed</span>}
        {differingCount > 0 && (
          <span className={styles.differingDot}>
            ● {differingCount} {t('device-config.bulk.differing')}
          </span>
        )}
        {section.advanced_toggle && (
          <div className={styles.advancedTogglePill} onClick={(e) => e.stopPropagation()}>
            <Switch label={t('device-config.common.show-advanced')} checked={showAdvanced} onChange={setShowAdvanced} />
          </div>
        )}
        {section.sub_profile && (
          <div className={styles.subProfilePill} onClick={(e) => e.stopPropagation()}>
            <label>{t(section.sub_profile.label_key)}</label>
            <select
              value={deriveSubProfile(section.sub_profile, values)}
              onChange={(e) => applySubProfile(section.sub_profile!, e.target.value, initialValues, onChange)}
            >
              {section.sub_profile.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.label_key)}
                </option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          className={styles.chev}
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>
      <div className={styles.sectionBody}>{body}</div>
    </section>
  );
};

export default SectionCard;
